import React from 'react';
import cn from 'classnames';

import { Meta, Tooltip } from '@components/common';

import { Icons } from '@icons/icons';

import colors from '@constants/colors';
import { regExpNumbers } from '@constants/regExp';

import styles from './NumberInput.module.scss';

const STEP = 0.5;
const DELAY_FOR_TOOLTIP = 1500;

interface INumberInputProps {
	value?: string;
	step?: number;
	name: string;
	disabled?: boolean;
	errorMessage?: string;
	placeholder?: string;
	maxValue?: number;
	maxChars?: number;
	minValue?: number;
	isError?: boolean;
	isClearable?: boolean;
	withTooltip?: boolean;
	onBlur?: () => void;
	onFocus?: () => void;
	onChange?: (value: string) => void;
	label?: string;
}

const NumberInput = ({
	value = '',
	name,
	step = STEP,
	maxChars,
	disabled,
	errorMessage,
	placeholder,
	isError = false,
	isClearable = true,
	withTooltip = false,
	onBlur,
	onFocus,
	onChange,
	minValue = 0,
	maxValue,
	label,
}: INumberInputProps) => {
	const inputClasses = cn(styles.numberInput, {
		[styles.f5gInputError]: !!errorMessage || isError,
	});
	const arrowsColor = disabled ? colors.gray50 : colors.indigo70;
	const disabledIncrease = !!disabled
		|| (value && maxValue ? (Number(value) >= Number(maxValue)) : false)
		|| !!(maxChars && ((Number(value) + 1).toString().length > maxChars));
	const disabledDecrease = !!disabled || (value ? Number(value) <= minValue : false);

	const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;

		if (maxChars && value.length > maxChars) return;

		const numberValue = value.replace(regExpNumbers, '');

		if (onChange) onChange(numberValue);
	};

	const increaseHandler = () => {
		if (disabledIncrease) return;

		if (onChange) {
			onChange(value
				? (Number(value) + step).toString()
				: (maxValue?.toString() || step.toString()),
			);
		}
	};

	const decreaseHandler = () => {
		if (disabledDecrease) return;

		if (onChange) {
			const updatedValue = value && value !== '0' ? (Number(value) - step) : minValue;

			onChange(updatedValue >= minValue
				? updatedValue.toString()
				: minValue.toString(),
			);
		}
	};

	const onClear = () => !disabled && onChange && onChange('');

	return (
		<>
			<div className={styles.numberInputContainer}>
				<Tooltip
					content={placeholder}
					delay={[DELAY_FOR_TOOLTIP, null]}
					isDisabled={!withTooltip}
				>
					<input
						className={inputClasses}
						data-testid={name}
						disabled={disabled}
						id={name}
						name={name}
						onBlur={onBlur}
						onChange={onChangeHandler}
						onFocus={onFocus}
						placeholder={placeholder}
						value={value}
					/>
				</Tooltip>
				{isClearable && !disabled && !!value.length
					&& <span
						className={styles.cross}
						onClick={onClear}
					>
					{Icons.cross()}
			</span>
				}
				<div className={styles.arrows}>
					<div
						className={cn(styles.arrow, { [styles.disabled]: disabledIncrease })}
						data-testid='arrow-increase'
						onClick={increaseHandler}
					>
						{Icons.increaseIcon({ color: arrowsColor })}
					</div>
					<div
						className={cn(styles.arrow, { [styles.disabled]: disabledDecrease })}
						data-testid='arrow-decrease'
						onClick={decreaseHandler}
					>
						{Icons.decreaseIcon({ color: arrowsColor })}
					</div>
				</div>
				{label
					&& <div className={styles.numberInputLabelContainer}>
						<label
							className={styles.numberInputLabel}
							htmlFor={name}
						>
							{label}
						</label>
					</div>
				}
			</div>
			{errorMessage && <Meta message={errorMessage} />}
		</>
	);
};

export default NumberInput;
